import { useCallback, useEffect, useState } from 'react';
import storeLocationsService from '../services/storeLocationsService';

type UseMapNewRegisterOptions = {
  storeId: string;
  onCreated?: () => void | Promise<void>;
};

export const useMapNewRegister = ({ storeId, onCreated }: UseMapNewRegisterOptions) => {
  const [registerId, setRegisterId] = useState('');
  const [storeLocationId, setStoreLocationId] = useState('');
  const [registerOptions, setRegisterOptions] = useState<string[]>([]);
  const [registerOptionsLoading, setRegisterOptionsLoading] = useState(false);
  const [registerOptionsError, setRegisterOptionsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!storeId) {
      setRegisterOptions([]);
      setRegisterOptionsLoading(false);
      setRegisterOptionsError('');
      return;
    }

    let cancelled = false;
    setRegisterOptionsLoading(true);
    setRegisterOptionsError('');

    const loadRegisterOptions = async () => {
      try {
        const registerInfo = await storeLocationsService.getStoreRegisterInfo(storeId);
        if (cancelled) {
          return;
        }

        setRegisterOptions(registerInfo.register_ids);
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        const message = loadError instanceof Error ? loadError.message : 'Failed to load register IDs.';
        setRegisterOptions([]);
        setRegisterOptionsError(message);
      } finally {
        if (!cancelled) {
          setRegisterOptionsLoading(false);
        }
      }
    };

    void loadRegisterOptions();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const handleSubmit = useCallback(async () => {
    if (!storeId) {
      setError('Store is not available.');
      return;
    }

    if (!registerId.trim() || !storeLocationId) {
      setError('Please select a location and enter a register ID.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await storeLocationsService.createStoreLocationRegister(storeId, {
        active: true,
        register_id: registerId.trim(),
        store_location_id: storeLocationId,
      });
      setRegisterId('');
      setStoreLocationId('');
      await onCreated?.();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Failed to map register.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [onCreated, registerId, storeId, storeLocationId]);

  return {
    error,
    registerOptions,
    registerOptionsError,
    registerOptionsLoading,
    registerId,
    setRegisterId,
    setStoreLocationId,
    storeLocationId,
    submitting,
    handleSubmit,
  };
};
